"use client";

import { useAuth, useUser } from "@clerk/nextjs";
import { createContext, useContext, useMemo } from "react";

import type { ReactNode } from "react";

type ClerkAuth = ReturnType<typeof useAuth>;

type AuthStateKey = "isLoaded" | "isSignedIn" | "userId" | "sessionId" | "getToken" | "signOut";

/**
 * The member's auth state as plain values, so a component re-renders only when
 * one of them changes. The same fields, and the same loaded / signed-in /
 * signed-out narrowing, as Clerk's `useAuth()`. `getToken` and `signOut` are
 * stable for the life of the page — Clerk builds them once per instance.
 */
export type AuthState = ClerkAuth extends infer Variant
  ? Variant extends unknown
    ? Pick<Variant, Extract<AuthStateKey, keyof Variant>>
    : never
  : never;

const AuthStateContext = createContext<AuthState | null>(null);

/**
 * The few things the UI shows about the signed-in member, as plain values.
 * `null` while signed out or before Clerk has loaded.
 */
export interface CurrentUserProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  imageUrl: string;
  /** Clerk `publicMetadata.role === "admin"`; display only, never an authorization check. */
  isAdmin: boolean;
}

const CurrentUserProfileContext = createContext<CurrentUserProfile | null | undefined>(
  undefined
);

/**
 * Reads Clerk once, near the root, and hands the rest of the app primitives.
 *
 * Why this exists (TX-5255, Sentry "Session long task" T3): Clerk's `useAuth`,
 * `useUser` and `useSession` are `useSyncExternalStore` subscriptions whose
 * snapshot is a new object on EVERY Clerk emit — and Clerk emits whenever a
 * Frontend API response carries the client, which includes the session `touch`
 * it sends each time the window regains focus. `useSyncExternalStore` updates
 * in React's synchronous lane, so every component calling one of those hooks
 * re-rendered inside Clerk's response handler. On the dashboard that was the
 * whole page (`useOnboarding` → `MainPageContent`): 431 component renders for
 * one touch that changed nothing a component reads. Production billed it as a
 * 1055ms frame inside `clerk.browser.js`'s `Response.json.then`.
 *
 * This provider still re-renders on each emit, but it renders only its
 * `children` element (unchanged, so React skips the subtree) and a context
 * value memoized on primitives, so consumers stay put unless the member, the
 * session or the loaded/signed-in state actually changed.
 *
 * Use `useAuthState()` instead of `useAuth()`, and `useCurrentUserProfile()`
 * instead of `useUser()`, anywhere that only needs who is signed in.
 */
export function AuthStateProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, sessionId, getToken, signOut } = useAuth();
  const { user } = useUser();

  const value = useMemo(
    // The fields arrive together from one Clerk snapshot, so they always form
    // one of the variants; TypeScript cannot see that through destructuring.
    () => ({ isLoaded, isSignedIn, userId, sessionId, getToken, signOut }) as AuthState,
    [isLoaded, isSignedIn, userId, sessionId, getToken, signOut]
  );

  const profileId = user?.id;
  const firstName = user?.firstName ?? null;
  const lastName = user?.lastName ?? null;
  const fullName = user?.fullName ?? null;
  const imageUrl = user?.imageUrl ?? "";
  const isAdmin = user?.publicMetadata?.role === "admin";
  const profile = useMemo<CurrentUserProfile | null>(
    () =>
      profileId
        ? { id: profileId, firstName, lastName, fullName, imageUrl, isAdmin }
        : null,
    [profileId, firstName, lastName, fullName, imageUrl, isAdmin]
  );

  return (
    <AuthStateContext.Provider value={value}>
      <CurrentUserProfileContext.Provider value={profile}>
        {children}
      </CurrentUserProfileContext.Provider>
    </AuthStateContext.Provider>
  );
}

/**
 * Who is signed in, without subscribing to Clerk's resource objects.
 * Must be rendered under `AuthStateProvider` (the root layout mounts it).
 */
export function useAuthState(): AuthState {
  const value = useContext(AuthStateContext);
  if (!value) {
    throw new Error("useAuthState must be used within AuthStateProvider");
  }
  return value;
}

/**
 * The signed-in member's name, avatar and admin flag, without subscribing to
 * Clerk's user object. Must be rendered under `AuthStateProvider`.
 */
export function useCurrentUserProfile(): CurrentUserProfile | null {
  const value = useContext(CurrentUserProfileContext);
  if (value === undefined) {
    throw new Error("useCurrentUserProfile must be used within AuthStateProvider");
  }
  return value;
}
